/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormHelperText,
} from '@mui/material';
import { logger } from '@musedrops/shared';
import type { ShowDynamics } from '@musedrops/shared';
import { getAllShowDynamics } from '../../data/showDynamics';

interface ShowDynamicsSelectorProps {
  value: string | null;
  onChange: (dynamicsKey: string | null) => void;
  label?: string;
  required?: boolean;
  helperText?: string;
}

export const ShowDynamicsSelector: React.FC<ShowDynamicsSelectorProps> = ({
  value,
  onChange,
  label = 'Dynamics',
  required = false,
  helperText,
}) => {
  const [dynamics, setDynamics] = useState<ShowDynamics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadDynamics = async () => {
      try {
        setLoading(true);
        const allDynamics = await getAllShowDynamics();
        setDynamics(allDynamics);
        setError('');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load show dynamics'
        );
        logger.error('Failed to load show dynamics', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadDynamics();
  }, []);

  // Find the selected dynamics for display of goodFor
  const selectedDynamics = dynamics.find(d => d.key === value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          onChange={e =>
            onChange(e.target.value === '' ? null : e.target.value)
          }
          label={label}
          disabled={loading}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {dynamics.map(d => (
            <MenuItem key={d.key} value={d.key}>
              {d.key}
            </MenuItem>
          ))}
        </Select>
        {selectedDynamics && (
          <FormHelperText>{selectedDynamics.instructions}</FormHelperText>
        )}
        {!selectedDynamics && helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
